import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import { ReservationSummaryApiService } from '../../services/reservation-summary-api.service';

import { debounceTime, filter, map, startWith, Subject, Subscription, take } from 'rxjs';

import {
	BankAccount,
	DocumentToSend,
	FilteredOrganization,
	PaymentDetails,
	PaymentKind,
	Reservation,
	UserData,
	User,
	Location,
} from '../../../reservation.model';

import { DataBagService } from '@/common/data-bag.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { ReservationService } from '../../../reservation.service';
import { DateAdapter } from '@angular/material/core';
import { ReservationSummaryPaymentsUserdataDialogComponent } from '../reservation-summary-payments-userdata-dialog/reservation-summary-payments-userdata-dialog.component';
import { PaymentKindId } from '@/common/enums/PaymentKind';
import { DocumentDownloader } from '../../reservation-summary-documents/document-downloader';
import { CancellationStatuses, Status } from '@/common/enums/Status';
import { LocationKinds } from '@/common/enums/LocationKinds';
import { AddressKind } from '@/common/enums/AddressKind';
import { ReservationSummaryStoreService } from '../../services/reservation-summary-store.service';
import { countryFromLocation } from '@/_utils/country-from-location';

type DialogData = {
	reservationId: number;
	data: PaymentDetails;
	generatePaymentConfirm: boolean;
};

@Component({
	selector: 'app-reservation-summary-payments-dialog',
	templateUrl: './reservation-summary-payments-dialog.component.html',
	styleUrls: ['./reservation-summary-payments-dialog.component.scss'],
})
export class ReservationSummaryPaymentsDialogComponent implements OnInit, OnDestroy {
	data: PaymentDetails;
	organization: FilteredOrganization;
	filteredOrganizations: FilteredOrganization[];
	filteredUsers: UserData[];
	paymentKinds: PaymentKind[] = [];
	incomingBankAccounts: BankAccount[] = [];
	organizations: FilteredOrganization[] = [];
	users: UserData[] = [];
	isOrganization = false;
	organizationsSelect = new FormControl(this.organizations);
	usersSelect = new FormControl(this.users);
	private participantDialogSubscription: Subscription;
	protected cashGenerate = false;
	private readonly cashDocumentSubject = new Subject<number>();
	private cashDocumentSubscription: Subscription;
	private loggedUser: User;

	constructor(
		public dialogRef: MatDialogRef<ReservationSummaryPaymentsDialogComponent>,
		private readonly api: DataBagService,
		private readonly date: DateService,
		private readonly infobox: InfoboxService,
		private readonly _service: ReservationService,
		@Inject(MAT_DIALOG_DATA) private readonly dialog: DialogData,
		private readonly dateAdapter: DateAdapter<Date>,
		private readonly apiService: ReservationSummaryApiService,
		private readonly store: ReservationSummaryStoreService,
		private readonly dialogHandle: MatDialog,
		private readonly documentDownloader: DocumentDownloader,
	) {
		this.dateAdapter.setLocale('pl');
	}

	ngOnInit(): void {
		this.data = this.dialog.data ? this.dialog.data : this.emptyPaymentDetails;
		this.isOrganization = !!this.data.organization;
		const { loggedUser } = this.store.getters();
		loggedUser().subscribe((value) => {
			this.loggedUser = value;
		});
		if (this.dialog.generatePaymentConfirm) {
			this.cashGenerate = true;
			this.dialogRef.disableClose = true;
			return this.generateCashConfirmation(this.data.id);
		}
		let reservation: Reservation = null;

		this._service.reservation$
			.pipe(
				take(1),
				filter((v) => !!v),
			)
			.subscribe((v) => (reservation = v));

		if (!this.dialog.data) {
			this.isOrganization = !!reservation.ownerOrganization;
			if (reservation.ownerOrganization) {
				this.switchPayerType();
				this.data.organization = reservation.ownerOrganization.id;
			} else {
				this.data.userData = reservation.ownerUser;
			}
		}

		this.paymentKinds = this._service.paymentKinds;
		this.incomingBankAccounts = this._service.incomingBankAccounts.filter((v) => v.mainAccount);
		this.organizations = this.getIsNotOskar(this._service.organizations);
		this.users = this._service.users;

		if (!this.newPayment && this.data.organization !== null) {
			this.loadOrganizationData();
		}

		this.filteredOrganizations = this.organizations;
		this.filteredUsers = this.users;
		this.organizationsSelect.valueChanges
			.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this._filter(value || '')),
			)
			.subscribe((filtered) => {
				this.filteredOrganizations = filtered;
			});

		this.usersSelect.valueChanges
			.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this._filterUsers(value || '')),
			)
			.subscribe((filtered) => {
				this.filteredUsers = filtered;
			});
	}

	ngOnDestroy() {
		this.participantDialogSubscription?.unsubscribe();
		this.cashDocumentSubscription?.unsubscribe();
		this.documentDownloader.documentUnsubscribe$?.next();
		this.documentDownloader.documentUnsubscribe$?.complete();
	}

	get newPayment() {
		return this.data?.id === null;
	}

	get currencies() {
		return this._service.currencies;
	}

	emptyPaymentDetails: PaymentDetails = {
		amount: null,
		bankAccountIncoming: '',
		bankAccountPosting: null,
		currency: {
			id: 1,
			name: 'Polski Złoty',
			code: 'PLN',
			exchangeRatePln: 100,
		},
		id: null,
		invoiceNumber: '',
		kind: {
			id: PaymentKindId.TRANSFER,
			description: '',
		},
		organization: null,
		payment: this.date.toUtcIso(new Date()),
		posting: this.date.toUtcIso(new Date()),
		status: 'CO',
		tableId: 1,
		tableName: 'reservation_reservation',
		userData: null,
		note: '',
	};

	displayFn(organization: FilteredOrganization | number): string {
		if (organization && typeof organization === 'number') {
			return this.organizations.find((v) => v.id === organization).name;
		} else if (organization && typeof organization === 'object' && !(organization instanceof Array))
			return organization.name;
		else if (this.data.organization) {
			return this.organizations.find((v) => v.id === this.data.organization).name;
		} else return '';
	}

	/**
	 * @param {UserData} user - entire user data object
	 * @description Sadly, the user can be UserData or even the array, need to check all scenarios in order to correctly display user in the user input
	 * @returns {string} User firstname and lastname combined into one string
	 */
	displayUserFn(user: UserData): string {
		if (user && !Array.isArray(user)) return `${user.firstName} ${user.lastName}`;
		else if (this.data.userData) {
			return `${this.data.userData.firstName} ${this.data.userData.lastName}`;
		} else return '';
	}

	private _filter(value: any): FilteredOrganization[] {
		const { findOrganization } = this.apiService.actions();
		if (value && typeof value === 'string') {
			const filterValue = value.toLowerCase();
			findOrganization(filterValue).then((result) => (this.filteredOrganizations = this.getIsNotOskar(result)));
		}
		return this.filteredOrganizations;
	}

	private getIsNotOskar(elements: FilteredOrganization[]): FilteredOrganization[] {
		return elements.filter((v) => !v.isOskar);
	}

	private _filterUsers(value: any): UserData[] {
		const { findUserData } = this.apiService.actions();
		if (value && typeof value === 'string') {
			if (value.length < 3) return this._service.users;
			findUserData(value).then((result) => {
				this.filteredUsers = result;
			});
			return;
		}
		return this._service.users;
	}

	protected createNewPayment() {
		const payload = [
			{
				...this.data,
				kind: this.data.kind.id,
				tableId: this.dialog.reservationId,
				currency: this.data.currency.id,
				userData: this.userDataId,
			},
		];

		this.api.post('/payment/', payload, (result, { error }) => {
			if (error) return;
			else {
				if (this.data.kind.id === PaymentKindId.CASH) this.cashDocumentSubject.next(result[0].id);
				else {
					this.infobox.success('Pomyślnie utworzono nową płatność');
					this.dialogRef.close(true);
				}
			}
		});
	}

	get userDataId(): number {
		if (!this.data.userData) return null;
		return typeof this.data.userData === 'number' ? this.data.userData : this.data.userData.id;
	}

	get isCashSelected(): boolean {
		return this.data.kind.id === PaymentKindId.CASH;
	}

	protected updatePayment() {
		const payload = {
			...this.data,
			kind: this.data.kind.id,
			userData: this.userDataId,
		};
		this.api.put(`/payment/bulk_update/`, [payload], (result, { error }) => {
			if (error) return;
			else {
				if (this.data.kind.id === PaymentKindId.CASH) this.cashDocumentSubject.next(this.data.id);
				else {
					this.infobox.success('Płatność pomyślnie zaktualizowana');
					this.dialogRef.close(true);
				}
			}
		});
	}

	submit() {
		if (!this.data.kind || this.data.amount == null || !this.data.payment || !this.data.posting) {
			return this.infobox.error('Wypełnij wszystkie wymagane pola');
		}
		const datePipe = new DatePipe('pl');
		if (datePipe.transform(this.data.posting, 'yyyy-MM-dd') < datePipe.transform(this.data.payment, 'yyyy-MM-dd')) {
			return this.infobox.error('Data zaksięgowania nie może występować wcześniej niż data wpłaty');
		}
		this.data.amount = Math.round(this.data.amount);
		if (this.newPayment) this.createNewPayment();
		else this.updatePayment();
		if (this.data.kind.id !== PaymentKindId.CASH) {
			this.apiService.actions().getReservation(this._service.reservation$.getValue().id);
		}
		this.cashDocumentSubscription = this.cashDocumentSubject.subscribe((id: number) => {
			this.cashGenerate = true;
			this.dialogRef.disableClose = true;
			this.generateCashConfirmation(id);
		});
	}

	private get formatSellerObject() {
		const reservation = this._service.reservation$.getValue();
		const mainAddress = reservation.seller.address.find((item) => item.kind.id === AddressKind.HEADQUATERS); //find HQ address;
		return {
			name: reservation.seller.name,
			street: mainAddress?.address,
			zipCode: mainAddress?.zipCode,
			city: mainAddress?.location.name,
			nip: reservation.seller.nip,
			phones: [],
			bankAccount: 'brak',
			travelOrganizationGovernmentApproval: '',
		};
	}

	private generateCashConfirmation(id: number) {
		const cashConfirmationDocument = this._service.documentKinds.find(
			(kind) => kind.name === 'reservation_cash_confirmation',
		);
		const reservation = this._service.reservation$.getValue();
		const offer = reservation.offer.find((v) => !CancellationStatuses.includes(v?.status as Status));

		let locationWithoutParent: Omit<Location, 'parentLocation'> | null = null;
		for (const location of offer.product.location) {
			const foundCountry = countryFromLocation(location);
			if (foundCountry) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { parentLocation, ...rest } = foundCountry;
				locationWithoutParent = rest;
				break;
			}
		}

		const documentToSend: DocumentToSend = {
			name: cashConfirmationDocument.name,
			version: cashConfirmationDocument.version,
			params: {
				id: reservation.id,
				createdAt: new Date(),
				seller: this.formatSellerObject,
				payment: id,
				product: offer.product.name,
				location: locationWithoutParent,
				term: reservation.time,
				responsibleUser: `${this.loggedUser.firstName} ${this.loggedUser.lastName}`,
				client: `${reservation.ownerUser.firstName} ${reservation.ownerUser.lastName}`,
			},
			reservations: [reservation.id],
		};
		this.api.post(`/reservation/${reservation.id}/documents/`, documentToSend, async (result, error) => {
			if (result) {
				const downloadResult = await this.documentDownloader.download(result.id);
				if (!downloadResult) return;
				this.cashGenerate = false;
				if (!this.dialog.generatePaymentConfirm)
					this.infobox.success(
						this.newPayment ? 'Pomyślnie utworzono nową płatność' : 'Płatność pomyślnie zaktualizowana',
					);
				this.dialogRef.close(true);
				this.apiService.actions().getReservation(this._service.reservation$.getValue().id);
			}
		});
	}

	closeDialog() {
		this.dialogRef.close(false);
	}

	private loadOrganizationData() {
		this.api.get(`/organization/${this.data.organization}/`, (result: FilteredOrganization, { error }) => {
			if (error) this.infobox.error(error.detail);
			else this.organization = result;
		});
	}

	switchPayerType() {
		if (this.isOrganization) this.data.userData = null;
		else this.data.organization = null;
	}

	changeOrganization($event: MatAutocompleteSelectedEvent) {
		this.data.organization = $event.option.value;
	}

	changeUser($event: MatAutocompleteSelectedEvent) {
		this.data.userData = $event.option.value;
	}

	convertToGrosze(price): number {
		return price * 100;
	}

	openUserDataDialog() {
		this.participantDialogSubscription = this.dialogHandle
			.open(ReservationSummaryPaymentsUserdataDialogComponent, {})
			.afterClosed()
			.subscribe(() => {
				if (this._service.newPayer) {
					this.users.push(this._service.newPayer);
					this.data.userData = this._service.newPayer;
					this.usersSelect.setValue([this._service.newPayer]);
					this._service.newPayer = null;
				}
			});
	}

	onPaymentKindChange($event: PaymentKindId) {
		if ($event === PaymentKindId.CASH) {
			this.data.bankAccountPosting = null;
			this.data.bankAccountIncoming = '';
		}
	}
}
